/**
 * Traduction de l'interface dans la langue choisie.
 *
 * <p>L'interface est écrite en français. Pour une autre langue, chaque texte
 * affiché est cherché dans le dictionnaire de cette langue (src/i18n), relevé
 * dans le code par `scripts/textes-a-traduire.mjs` : les textes entre balises,
 * les attributs lisibles (placeholder, title, aria-label, alt) et les messages
 * des boîtes du navigateur. Un texte absent du dictionnaire reste en français,
 * et le test des dictionnaires signale ceux qui manquent.
 *
 * <p>Ce qui appartient à l'utilisateur n'est jamais touché : les champs de
 * saisie, et tout ce qui porte `translate="no"` — la feuille A4 du document,
 * qui suit la langue du document, ou une zone de contenu saisi.
 */

import { enregistreDictionnaire } from './i18n';
import { avecLeNom, NOM_D_ORIGINE, RENOMME } from './marque';

/**
 * Dictionnaires disponibles, chargés à la demande : un fichier par langue dans
 * src/i18n (de.json, it.json…). Une langue s'ajoute en y déposant son fichier.
 */
const DICTIONNAIRES = Object.fromEntries(
  Object.entries(import.meta.glob('../i18n/*.json')).map(([chemin, charge]) => [
    chemin.replace(/^.*\/([a-z-]+)\.json$/, '$1'),
    charge,
  ]),
);

/** Langues dans lesquelles l'interface est entièrement traduite. */
export const LANGUES_TRADUITES = ['fr', ...Object.keys(DICTIONNAIRES)];

/** Les langues qui s'écrivent de droite à gauche : l'interface s'y retourne. */
export const LANGUES_RTL = ['ar', 'fa', 'he', 'ur', 'ps', 'dv'];

/**
 * La langue du téléphone, si l'interface existe dans cette langue ; le
 * français sinon. « zh-TW » et « zh-HK » prennent le chinois traditionnel.
 *
 * @param {Navigator} [nav] navigateur (injectable pour les tests)
 * @returns {string} code de la langue de l'écran
 */
export function langueDuTelephone(nav = globalThis.navigator) {
  const demandees = [...(nav?.languages || []), nav?.language].filter(Boolean);
  for (const brut of demandees) {
    const code = String(brut).toLowerCase();
    if (/^zh-(tw|hk|mo|hant)/.test(code) && LANGUES_TRADUITES.includes('zh-tw')) return 'zh-tw';
    const base = code.split('-')[0];
    // Le norvégien « nb » / « nn » s'écrit « no » dans SwissTech.
    const langue = base === 'nb' || base === 'nn' ? 'no' : base;
    if (LANGUES_TRADUITES.includes(langue)) return langue;
  }
  return 'fr';
}

/** Donne la langue et le sens d'écriture à la page (lecteurs d'écran, polices, RTL). */
function marqueLaPage(langue) {
  const racine = document.documentElement;
  racine.lang = langue;
  racine.dir = LANGUES_RTL.includes(langue) ? 'rtl' : 'ltr';
}

const ATTRIBUTS = ['placeholder', 'title', 'aria-label', 'alt'];

/** Où ne jamais traduire : contenu de l'utilisateur, code. */
const A_NE_PAS_TRADUIRE = '[translate="no"], [data-notranslate], script, style, code, pre';

/** Zones de saisie : leur texte d'aide se traduit, jamais ce qui est saisi. */
const SAISIE = 'textarea, [contenteditable="true"]';

let actif = null; // { textes: Map, modeles: Array, observateur }

const normalise = (s) => s.replace(/\s+/g, ' ').trim();

const echappe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Prépare un dictionnaire : les textes fixes, et les modèles « Bonjour {0} »
 * compilés en expressions, rangés par leur premier mot fixe.
 */
export function prepare(dictionnaire) {
  const textes = new Map();
  const modeles = [];
  for (const [fr, trad] of Object.entries(dictionnaire || {})) {
    if (!trad || trad === fr) continue;
    if (/\{\d+\}/.test(fr)) {
      // Un modèle dont la partie fixe tient en deux lettres (« {0} à {1} »)
      // reconnaîtrait aussi bien un nom de client : il n'est pas retenu.
      if ((fr.replace(/\{\d+\}/g, '').match(/[A-Za-zÀ-ÿ]/g) || []).length < 3) continue;
      const parts = fr.split(/(\{\d+\})/);
      const ordre = [];
      const source = parts
        .map((p) => {
          const m = p.match(/^\{(\d+)\}$/);
          if (m) {
            ordre.push(Number(m[1]));
            return '(.*?)';
          }
          return echappe(p);
        })
        .join('');
      modeles.push({ fr, re: new RegExp('^' + source + '$'), ordre, trad });
    } else {
      textes.set(normalise(fr), trad);
    }
  }
  // Le modèle le plus précis d'abord : « Facture {0} envoyée » avant
  // « Facture {0} ».
  const fixe = (m) => m.fr.replace(/\{\d+\}/g, '').length;
  modeles.sort((a, b) => fixe(b) - fixe(a));
  return { textes, modeles };
}

/**
 * Traduit un texte, en gardant les espaces autour : « Bonjour » collé à un
 * nom garde son espace.
 *
 * @returns le texte traduit, ou null s'il n'est pas au dictionnaire
 */
export function traduis(brut, dico = actif) {
  if (!dico || typeof brut !== 'string') return null;
  const m = brut.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const cle = normalise(m[2]);
  if (!cle || !/[A-Za-zÀ-ÿ]/.test(cle)) return null;
  let trad = dico.textes.get(cle);
  // « Facture envoyée · 27 » : chaque morceau se traduit pour lui-même. Si
  // un morceau lisible reste en français, un modèle de la phrase entière
  // (« Essai gratuit · {0} jours restants ») passe avant.
  let partiel = null;
  if (!trad && cle.includes(' · ')) {
    const morceaux = cle.split(' · ');
    const traduits = morceaux.map((m) => traduis(m, dico));
    if (traduits.some((t) => t != null)) {
      const assemble = morceaux.map((m, i) => (traduits[i] != null ? traduits[i] : m)).join(' · ');
      const complet = morceaux.every((m, i) => traduits[i] != null || !/[A-Za-zÀ-ÿ]{2}/.test(m));
      if (complet) trad = assemble;
      else partiel = assemble;
    }
  }
  if (!trad) {
    for (const { re, ordre, trad: modele } of dico.modeles) {
      const r = cle.match(re);
      if (!r) continue;
      const valeurs = {};
      ordre.forEach((n, i) => {
        // Une valeur qui est elle-même un mot de l'interface (« Devis » dans
        // « {0} et factures ») se traduit aussi ; un nom de client, non.
        const v = r[i + 1];
        // Un mot de deux lettres (« le », « il ») reste tel quel : trop court pour
        // être reconnu à coup sûr.
        const lisible = (v.match(/[A-Za-zÀ-ÿ]/g) || []).length >= 3;
        valeurs[n] = (lisible && v.length < cle.length && traduis(v, dico)) || v;
      });
      // « {1|} » : la valeur est absorbée — une terminaison française (« s »)
      // que la phrase traduite n'emploie pas.
      trad = modele.replace(/\{(\d+)(\|?)\}/g, (x, n, muet) =>
        muet ? '' : valeurs[n] != null ? valeurs[n] : x,
      );
      break;
    }
  }
  // « · En cours », « 25 boîte » : le mot de l'interface collé à une puce
  // ou à une quantité se traduit seul ; la puce et le nombre restent.
  if (!trad) {
    const colle = cle.match(/^(·\s+|[\d'’.,]+(?:\s*[×x]\s*[\d'’.,]+)?\s+)(.+)$/);
    if (colle && /[A-Za-zÀ-ÿ]{2}/.test(colle[2])) {
      const suite = traduis(colle[2], dico);
      if (suite != null) trad = colle[1] + suite.trim();
    }
  }
  // « Matériel — Vis inox », « 1 × CHF 150.00 / forfait » : la catégorie ou
  // l'unité se traduit, le libellé saisi par l'utilisateur reste tel quel.
  if (!trad) {
    for (const sep of [' — ', ' / ']) {
      if (!cle.includes(sep)) continue;
      const morceaux = cle.split(sep);
      const traduits = morceaux.map((x) =>
        dico.textes.has(normalise(x)) ? traduis(x, dico) : null,
      );
      if (traduits.some((t) => t != null)) {
        trad = morceaux.map((x, i) => (traduits[i] != null ? traduits[i].trim() : x)).join(sep);
        break;
      }
    }
  }
  if (!trad) trad = partiel;
  // « maladie », « vacances » : un mot affiché tel qu'enregistré, en
  // minuscule, se trouve au dictionnaire sous sa majuscule.
  if (!trad && /^[a-zà-ÿ]/.test(cle))
    trad = dico.textes.get(cle.charAt(0).toUpperCase() + cle.slice(1));
  return avecLeNomDuLogiciel(trad ? m[1] + trad + m[3] : null, brut);
}

/**
 * Le logiciel renommé (src/marque.json) : son nom prend la place de
 * « SwissTech » dans le texte traduit, ou dans le texte français resté tel quel.
 */
function avecLeNomDuLogiciel(traduit, brut) {
  if (!RENOMME) return traduit;
  const texte = traduit ?? brut;
  return texte.includes(NOM_D_ORIGINE) ? avecLeNom(texte) : traduit;
}

function protege(element) {
  return (
    !element || (element.closest && (element.closest(A_NE_PAS_TRADUIRE) || element.closest(SAISIE)))
  );
}

/** Les attributs d'une zone de saisie se traduisent ; ceux d'un contenu protégé, non. */
function attributsProteges(element) {
  return !element || (element.closest && element.closest(A_NE_PAS_TRADUIRE));
}

/** Le parcours écarte d'un coup les branches protégées. */
const FILTRE = {
  acceptNode: (n) => {
    if (n.nodeType !== 1) return NodeFilter.FILTER_ACCEPT;
    if (n.matches(A_NE_PAS_TRADUIRE)) return NodeFilter.FILTER_REJECT;
    if (n.matches(SAISIE)) {
      traduisAttributs(n);
      return NodeFilter.FILTER_REJECT;
    }
    return NodeFilter.FILTER_ACCEPT;
  },
};

/** « facture{n > 1 ? 's' : ''} » : la terminaison française seule, sans objet dans une autre langue. */
const TERMINAISON = /^(s|e|es)$/;

function traduisTexte(n) {
  // En français, la terminaison reste : seul le nom du logiciel y change.
  if (!actif?.nomSeul && TERMINAISON.test(n.nodeValue) && n.previousSibling?.nodeType === 3) {
    n.nodeValue = '';
    return;
  }
  const t = traduis(n.nodeValue);
  if (t != null && t !== n.nodeValue) n.nodeValue = t;
}

function traduisNoeud(noeud) {
  if (noeud.nodeType === 3) {
    if (protege(noeud.parentElement)) return;
    traduisTexte(noeud);
    return;
  }
  if (noeud.nodeType !== 1 || attributsProteges(noeud)) return;
  traduisAttributs(noeud);
  if (protege(noeud)) return;
  const parcours = document.createTreeWalker(
    noeud,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    FILTRE,
  );
  for (let n = parcours.nextNode(); n; n = parcours.nextNode()) {
    if (n.nodeType === 1) traduisAttributs(n);
    else traduisTexte(n);
  }
}

function traduisAttributs(element) {
  for (const a of ATTRIBUTS) {
    const v = element.getAttribute && element.getAttribute(a);
    if (!v) continue;
    const t = traduis(v);
    if (t != null && t !== v) element.setAttribute(a, t);
  }
  if (element.tagName === 'INPUT' && /^(button|submit|reset)$/i.test(element.type)) {
    const t = traduis(element.value);
    if (t != null && t !== element.value) element.value = t;
  }
}

const boitesOrigine = {};

function traduisLesBoites() {
  for (const nom of ['alert', 'confirm', 'prompt']) {
    if (typeof window[nom] !== 'function' || boitesOrigine[nom]) continue;
    boitesOrigine[nom] = window[nom];
    window[nom] = (message, ...reste) =>
      boitesOrigine[nom].call(
        window,
        actif ? (traduis(String(message)) ?? message) : message,
        ...reste,
      );
  }
}

/**
 * Met l'interface dans la langue donnée. Le français, ou une langue sans
 * dictionnaire, arrête la traduction : l'interface reste telle qu'écrite.
 *
 * @param {string} langue code de la langue (fr, de…)
 * @returns {Promise<boolean>} vrai si une traduction est active
 */
export async function activeLaTraduction(langue) {
  const charge = DICTIONNAIRES[langue];
  if (!charge) {
    arreteLaTraduction();
    marqueLaPage('fr');
    // En français, seul le nom du logiciel change, s'il a été renommé.
    if (RENOMME) observeLaPage({ ...prepare({}), nomSeul: true });
    return false;
  }
  const module = await charge();
  const dictionnaire = module.default || module;
  // Les documents (feuille A4) traduisent leurs textes eux-mêmes, par tr().
  enregistreDictionnaire(langue, dictionnaire);
  arreteLaTraduction();
  marqueLaPage(langue);
  observeLaPage(prepare(dictionnaire));
  return true;
}

/** Traduit la page affichée, puis chaque texte qui y apparaît. */
function observeLaPage(dico) {
  actif = dico;
  traduisLesBoites();
  traduisNoeud(document.body);
  document.title = traduis(document.title) ?? document.title;
  const observateur = new MutationObserver((changements) => {
    for (const c of changements) {
      if (c.type === 'characterData') traduisNoeud(c.target);
      else if (c.type === 'attributes') {
        if (!attributsProteges(c.target)) traduisAttributs(c.target);
      } else c.addedNodes.forEach(traduisNoeud);
    }
  });
  observateur.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRIBUTS,
  });
  actif.observateur = observateur;
}

/** Arrête la traduction ; les textes déjà traduits le restent jusqu'au rechargement. */
export function arreteLaTraduction() {
  if (actif && actif.observateur) actif.observateur.disconnect();
  actif = null;
}
